import { useRef } from "react";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { FileExport, FileImport, Plus } from "components/UI/Icons";
import { ARROW_POSITION, Tooltip, TooltipContent } from "components/UI/Tooltip";
import { COLORS } from "constants/colors.constants";
import { MAX_PACKING_GROUPS } from "constants/common.constants";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useTranslation } from "localization/localization";
import { addGroup, selectGroupsCount } from "store";
import { ControlsBarProps } from "./ControlsBar.types";
import { RecentLoading } from "./RecentLoading";
import { UnitsDropdown } from "./UnitsDropdown";
import s from "./ControlsBar.module.scss";

export const ControlsBar = ({ onImport, onExport, exportDisabled }: ControlsBarProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const groupsCount = useAppSelector(selectGroupsCount);
  const importInputRef = useRef<HTMLInputElement>(null);
  const canAddGroup = groupsCount < MAX_PACKING_GROUPS;

  return (
    <div className={s.bar}>
      <div className={s.mainBox}>
        <Button
          variant={BUTTON_VARIANT.SECONDARY}
          onClick={() => dispatch(addGroup())}
          disabled={!canAddGroup}
          className={s.actionButton}
        >
          <Plus color={COLORS.DUBAI_100} /> {t("Add group")}
        </Button>
      </div>

      <RecentLoading buttonClassName={s.actionButton} />

      <input ref={importInputRef} type="file" accept=".xlsx, .xls" className={s.hiddenInput} onChange={onImport} />

      <Tooltip
        triggerElement={
          <Button
            variant={BUTTON_VARIANT.TERTIARY}
            className={s.actionButton}
            onClick={() => importInputRef.current?.click()}
          >
            <FileImport />
            {t("Import")}
          </Button>
        }
        offsetY={8}
        placement="bottom"
        enabledOnMobile={true}
      >
        <TooltipContent arrowPosition={ARROW_POSITION.TOP} className={s.tooltipContent}>
          {t(
            'Make sure the format is .xls, .xlsx Make sure there are no additional tabs in the file. Make sure that the validity date format is "Date", last from the list "1-Jan-2000" Recommended to use country abbreviation (IN, US etc.) Avoid empty cells. Use whole numbers without comma. Avoid texts or other non-numeric values in rate cells Ignoring these guidelines may cause a mistake in file uploading.'
          )}
        </TooltipContent>
      </Tooltip>

      <Button variant={BUTTON_VARIANT.TERTIARY} className={s.actionButton} onClick={onExport} disabled={exportDisabled}>
        <FileExport />
        {t("Export")}
      </Button>

      <UnitsDropdown buttonClassName={s.actionButton} />
    </div>
  );
};
